<template>
    <div class="rule-conditions">
        <div class="d-flex align-items-center mb-3">
            <span class="mr-2">{{ $t('RC_COMBINE') }}</span>
            <b-form-select v-model="operator" :options="operatorOptions" class="w-auto"/>
            <span class="ml-2 text-muted small">
                {{ operator === 'and' ? $t('RC_ALL_HINT') : $t('RC_ANY_HINT') }}
            </span>
            <b-button class="ml-auto" size="sm" variant="outline-primary" v-on:click.prevent="add">
                <i class="mdi mdi-plus"></i> {{ $t('RC_ADD') }}
            </b-button>
        </div>

        <div v-if="loading" class="text-muted small">{{ $t('HISTORY_LOADING') }}</div>

        <div v-else-if="rows.length === 0" class="text-muted small mb-3">{{ $t('RC_EMPTY') }}</div>

        <table v-else class="table table-sm table-striped">
            <thead>
                <tr>
                    <th style="width: 22%;">{{ $t('MODULE') }}</th>
                    <th style="width: 20%;">{{ $t('RC_SOURCE') }}</th>
                    <th style="width: 14%;">{{ $t('RC_AGGREGATE') }}</th>
                    <th style="width: 18%;">{{ $t('RC_WINDOW') }}</th>
                    <th style="width: 10%;">{{ $t('RC_OPERATOR') }}</th>
                    <th style="width: 12%;">{{ $t('RC_VALUE') }}</th>
                    <th style="width: 4%;"></th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="(row, index) in rows" :key="index">
                    <td>
                        <b-form-select v-model="row.moduleId" v-on:change="resetSource(row)">
                            <option v-for="one in sources" :key="one.moduleId" :value="one.moduleId">
                                {{ one.moduleName }}
                            </option>
                        </b-form-select>
                    </td>
                    <td>
                        <b-form-select v-model="row.source">
                            <option v-for="name in sourcesFor(row.moduleId)" :key="name" :value="name">
                                {{ sourceLabel(name) }}
                            </option>
                        </b-form-select>
                    </td>
                    <td>
                        <b-form-select v-model="row.aggregate" :options="aggregateOptions"/>
                    </td>
                    <td>
                        <div class="d-flex" v-if="row.aggregate !== 'current'">
                            <b-form-input type="number" min="1" v-model.number="row.windowValue" class="mr-1"/>
                            <b-form-select v-model="row.windowUnit" :options="unitOptions"/>
                        </div>
                        <span v-else class="text-muted small">{{ $t('RC_NOW') }}</span>
                    </td>
                    <td>
                        <b-form-select v-model="row.operator" :options="comparisonOptions"/>
                    </td>
                    <td>
                        <b-form-input type="number" step="any" v-model.number="row.compareValue"/>
                    </td>
                    <td class="text-right">
                        <b-button size="sm" variant="link" class="text-danger p-0"
                            v-on:click.prevent="remove(index)">
                            <i class="mdi mdi-delete"></i>
                        </b-button>
                    </td>
                </tr>
            </tbody>
        </table>

        <p v-if="error" class="text-danger small">{{ error }}</p>

        <b-button variant="primary" size="sm" :disabled="saving" v-on:click.prevent="save">
            {{ $t('SAVE') }}
        </b-button>
    </div>
</template>

<script>
import Api from '@/api/Rules';

// minutes per unit, so the form can talk in hours and days and still store one
// number the engine understands
const UNITS = { minutes: 1, hours: 60, days: 1440 };

export default
{
    name: 'RuleConditions',
    props:
    {
        ruleId:
        {
            type: [Number, String],
            required: true,
        },
        // [{ moduleId, moduleName, sources: [...] }] from Rules/getConditionSources
        sources:
        {
            type: Array,
            default: () => [],
        },
        conditionOperator:
        {
            type: String,
            default: 'and',
        },
    },
    data()
    {
        const me = this;

        return {
            rows: [],
            operator: me.conditionOperator || 'and',
            loading: false,
            saving: false,
            error: '',
            operatorOptions: [
                { value: 'and', text: me.$i18n.t('RC_AND') },
                { value: 'or', text: me.$i18n.t('RC_OR') },
            ],
            aggregateOptions: [
                { value: 'current', text: me.$i18n.t('RC_AGG_CURRENT') },
                { value: 'avg', text: me.$i18n.t('RC_AGG_AVG') },
                { value: 'min', text: me.$i18n.t('RC_AGG_MIN') },
                { value: 'max', text: me.$i18n.t('RC_AGG_MAX') },
                { value: 'sum', text: me.$i18n.t('RC_AGG_SUM') },
                { value: 'delta', text: me.$i18n.t('RC_AGG_DELTA') },
            ],
            unitOptions: [
                { value: 'minutes', text: me.$i18n.t('RC_MINUTES') },
                { value: 'hours', text: me.$i18n.t('RC_HOURS') },
                { value: 'days', text: me.$i18n.t('RC_DAYS') },
            ],
            comparisonOptions: ['>', '>=', '<', '<=', '=', '!='],
        };
    },
    created()
    {
        this.load();
    },
    watch:
    {
        ruleId()
        {
            this.load();
        },
        conditionOperator(value)
        {
            this.operator = value || 'and';
        },
    },
    methods:
    {
        sourcesFor(moduleId)
        {
            const found = this.sources.find((one) => String(one.moduleId) === String(moduleId));

            return (found == null) ? [] : found.sources;
        },
        // 'value:dew_point' is how it is stored, 'dew point' is how it reads
        sourceLabel(name)
        {
            return (name.indexOf('value:') === 0) ? name.substr(6).replace(/_/g, ' ') : name;
        },
        resetSource(row)
        {
            const available = this.sourcesFor(row.moduleId);

            if (available.indexOf(row.source) === -1)
            {
                row.source = available[0] || '';
            }
        },
        /**
         * The engine stores one number of minutes. The form shows whichever unit
         * divides it exactly, so 10080 comes back as 7 days rather than as itself.
         */
        toWindow(minutes)
        {
            const total = parseInt(minutes, 10);

            if (Number.isNaN(total) || total <= 0)
            {
                return { windowValue: 1, windowUnit: 'hours' };
            }

            if (total % UNITS.days === 0)
            {
                return { windowValue: total / UNITS.days, windowUnit: 'days' };
            }

            if (total % UNITS.hours === 0)
            {
                return { windowValue: total / UNITS.hours, windowUnit: 'hours' };
            }

            return { windowValue: total, windowUnit: 'minutes' };
        },
        add()
        {
            const me = this;
            const first = me.sources[0];

            me.rows.push({
                moduleId: (first == null) ? '' : first.moduleId,
                source: (first == null || first.sources.length === 0) ? '' : first.sources[0],
                aggregate: 'current',
                windowValue: 1,
                windowUnit: 'hours',
                operator: '>',
                compareValue: 0,
            });
        },
        remove(index)
        {
            this.rows.splice(index, 1);
        },
        async load()
        {
            const me = this;

            me.loading = true;
            me.error = '';

            try
            {
                const data = await Api.getConditions({ ruleId: me.ruleId });

                me.rows = (data.data || []).map((row) => (
                    {
                        moduleId: row.moduleId,
                        source: row.source,
                        aggregate: row.aggregate,
                        operator: row.operator,
                        compareValue: Number(row.compareValue),
                        ...me.toWindow(row.windowMinutes),
                    }));
            }
            catch (error)
            {
                me.error = (error && error.message) ? error.message : 'Error';
            }

            me.loading = false;
        },
        async save()
        {
            const me = this;

            me.saving = true;
            me.error = '';

            const conditions = me.rows.map((row) => (
                {
                    moduleId: row.moduleId,
                    source: row.source,
                    aggregate: row.aggregate,
                    windowMinutes: (row.aggregate === 'current')
                        ? null
                        : Math.max(1, Math.round(row.windowValue * UNITS[row.windowUnit])),
                    operator: row.operator,
                    compareValue: row.compareValue,
                }));

            try
            {
                const data = await Api.saveConditions({
                    ruleId: me.ruleId,
                    conditionOperator: me.operator,
                    conditions,
                });

                if (data && data.error)
                {
                    me.error = data.notice || 'Error';
                }
                else
                {
                    me.$emit('saved', {
                        ruleId: me.ruleId,
                        count: conditions.length,
                        conditionOperator: me.operator,
                    });
                }
            }
            catch (error)
            {
                me.error = (error && error.message) ? error.message : 'Error';
            }

            me.saving = false;
        },
    },
};
</script>
